<script setup>
/* global axios */
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import ButtonV4 from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import ConversationAPI from 'dashboard/api/conversations.js';
import RemoteSupportIdentitiesAPI from 'dashboard/api/remoteSupportIdentities.js';
import RemoteControlDropdown from './RemoteControlDropdown.vue';
import AddIdPopup from './AddIdPopup.vue';

const store = useStore();
const { t } = useI18n();

const identities = ref([]);
const isRequestingId = ref(false);
const isSavingIdentity = ref(false);
const isDropdownOpen = ref(false);
const isAddPopupOpen = ref(false);
const addPopupAutoConnect = ref(false);
const identityPendingArchive = ref(null);

const confirmArchiveDialogRef = ref(null);

const currentChat = computed(() => store.getters.getSelectedChat);

const isRemoteSupportEnabled = computed(() => {
  const integration = store.getters['integrations/getIntegration'](
    'remote_support',
    null
  );
  return integration && integration.hooks && integration.hooks.length > 0;
});

const contactId = computed(() => currentChat.value?.meta?.sender?.id);

const fetchIdentities = async () => {
  if (!contactId.value) {
    identities.value = [];
    return;
  }
  try {
    const { data } = await RemoteSupportIdentitiesAPI.get(contactId.value);
    identities.value = Array.isArray(data) ? data : [];
  } catch (error) {
    identities.value = [];
  }
};

// Fire whenever contactId or integration-loaded state changes, including the
// first time they both resolve to truthy. Previously an `onMounted` gated on
// `isRemoteSupportEnabled` could run before the integrations store populated,
// leaving the list empty until a write triggered a manual refetch.
watch(
  [contactId, isRemoteSupportEnabled],
  ([newContactId, enabled]) => {
    if (enabled && newContactId) {
      fetchIdentities();
    }
  },
  { immediate: true }
);

// URL scheme derived from the rebranded client's appname/exename at rdgen
// build time (e.g. appname="NexyomRemote" → scheme "nexyomremote://"). The
// `connect/` authority is required by RustDesk >= 1.2.1 — a bare scheme://<id>
// no longer dispatches.
const REMOTE_CLIENT_URL_SCHEME = 'nexyomremote';

const launchRustdesk = (rustdeskId, rustdeskPassword) => {
  const base = `${REMOTE_CLIENT_URL_SCHEME}://connect/${rustdeskId}`;
  window.location.href = rustdeskPassword
    ? `${base}?password=${encodeURIComponent(rustdeskPassword)}`
    : base;
};

const touchIdentity = async identityId => {
  try {
    await RemoteSupportIdentitiesAPI.touch(contactId.value, identityId);
  } catch (error) {
    // Non-fatal: stamping last_used_at failed but connection already launched.
  }
};

const sendRemoteSupportRequest = async () => {
  if (isRequestingId.value || !currentChat.value?.id) return;
  isRequestingId.value = true;
  try {
    await axios.post(
      `${ConversationAPI.url}/${currentChat.value.id}/remote_support`
    );
    useAlert(t('CONVERSATION.REMOTE_SUPPORT.SUCCESS'));
  } catch (error) {
    const message =
      error?.response?.data?.error || t('CONVERSATION.REMOTE_SUPPORT.ERROR');
    useAlert(message);
  } finally {
    isRequestingId.value = false;
  }
};

const openAddPopup = ({ autoConnect }) => {
  addPopupAutoConnect.value = autoConnect;
  isAddPopupOpen.value = true;
  isDropdownOpen.value = false;
};

const closeAddPopup = () => {
  isAddPopupOpen.value = false;
};

const onRemoteControlClick = async () => {
  if (!contactId.value) {
    useAlert(t('CONVERSATION.REMOTE_SUPPORT.NO_CONTACT'));
    return;
  }
  if (identities.value.length === 0) {
    openAddPopup({ autoConnect: true });
  } else if (identities.value.length === 1) {
    const [only] = identities.value;
    launchRustdesk(only.rustdesk_id, only.rustdesk_password);
    await touchIdentity(only.id);
    await fetchIdentities();
  } else {
    isDropdownOpen.value = !isDropdownOpen.value;
  }
};

const toggleDropdown = () => {
  isDropdownOpen.value = !isDropdownOpen.value;
};

const closeDropdown = () => {
  isDropdownOpen.value = false;
};

const onDropdownConnect = async identity => {
  launchRustdesk(identity.rustdesk_id, identity.rustdesk_password);
  await touchIdentity(identity.id);
  isDropdownOpen.value = false;
  await fetchIdentities();
};

const onDropdownArchive = identity => {
  identityPendingArchive.value = identity;
  isDropdownOpen.value = false;
  confirmArchiveDialogRef.value?.open();
};

const handleArchiveConfirm = async () => {
  const identity = identityPendingArchive.value;
  if (!identity) return;
  try {
    await RemoteSupportIdentitiesAPI.archive(contactId.value, identity.id);
    await fetchIdentities();
    useAlert(t('CONVERSATION.REMOTE_SUPPORT.ARCHIVE_SUCCESS'));
  } catch (error) {
    useAlert(t('CONVERSATION.REMOTE_SUPPORT.ARCHIVE_ERROR'));
  } finally {
    identityPendingArchive.value = null;
    confirmArchiveDialogRef.value?.close();
  }
};

const handleArchiveClose = () => {
  identityPendingArchive.value = null;
};

const onAddSubmit = async ({ rustdeskId, label, rustdeskPassword }) => {
  if (isSavingIdentity.value) return;
  isSavingIdentity.value = true;
  try {
    const { data: identity } = await RemoteSupportIdentitiesAPI.create(
      contactId.value,
      { rustdeskId, label, rustdeskPassword }
    );
    if (addPopupAutoConnect.value && identity?.rustdesk_id) {
      launchRustdesk(identity.rustdesk_id, identity.rustdesk_password);
      await touchIdentity(identity.id);
    }
    isAddPopupOpen.value = false;
    await fetchIdentities();
  } catch (error) {
    const message =
      error?.response?.data?.errors?.join(', ') ||
      error?.response?.data?.error ||
      t('CONVERSATION.REMOTE_SUPPORT.SAVE_ERROR');
    useAlert(message);
  } finally {
    isSavingIdentity.value = false;
  }
};

const archiveDialogDescription = computed(() => {
  const id = identityPendingArchive.value?.rustdesk_id || '';
  return t('CONVERSATION.REMOTE_SUPPORT.ARCHIVE_CONFIRM', { id });
});
</script>

<template>
  <div
    v-if="isRemoteSupportEnabled"
    class="remote-support-actions inline-flex gap-2 items-center"
  >
    <ButtonV4
      v-tooltip="$t('CONVERSATION.REMOTE_SUPPORT.REQUEST_ID_TOOLTIP')"
      size="sm"
      variant="faded"
      color="slate"
      :label="$t('CONVERSATION.REMOTE_SUPPORT.REQUEST_ID_BUTTON')"
      :is-loading="isRequestingId"
      class="rounded-md"
      @click="sendRemoteSupportRequest"
    />

    <div
      v-on-clickaway="closeDropdown"
      class="relative inline-flex items-center"
    >
      <div class="inline-flex items-center gap-0.5">
        <ButtonV4
          size="sm"
          variant="faded"
          color="blue"
          :label="$t('CONVERSATION.REMOTE_SUPPORT.REMOTE_CONTROL_BUTTON')"
          icon="i-lucide-monitor"
          class="rounded-md"
          @click="onRemoteControlClick"
        />
        <ButtonV4
          size="sm"
          variant="faded"
          color="blue"
          icon="i-lucide-chevron-down"
          class="rounded-md"
          :aria-label="$t('CONVERSATION.REMOTE_SUPPORT.MANAGE_IDS')"
          @click="toggleDropdown"
        />
      </div>

      <RemoteControlDropdown
        v-if="isDropdownOpen"
        :identities="identities"
        @connect="onDropdownConnect"
        @archive="onDropdownArchive"
        @add-another="openAddPopup({ autoConnect: false })"
        @close="closeDropdown"
      />
    </div>

    <AddIdPopup
      v-if="isAddPopupOpen"
      :auto-connect="addPopupAutoConnect"
      :is-loading="isSavingIdentity"
      @submit="onAddSubmit"
      @close="closeAddPopup"
    />

    <Dialog
      ref="confirmArchiveDialogRef"
      type="alert"
      :title="$t('CONVERSATION.REMOTE_SUPPORT.ARCHIVE_TITLE')"
      :description="archiveDialogDescription"
      :confirm-button-label="$t('CONVERSATION.REMOTE_SUPPORT.ARCHIVE')"
      @confirm="handleArchiveConfirm"
      @close="handleArchiveClose"
    />
  </div>
</template>
